import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Timestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { OptionalTransactionType, Distribution, TransactionStatus, TransactionSource, AllTransactionType } from '../global/types';
import { selectSettings } from '../store/authSlice';
import { selectAccounts } from '../store/userDataSlice';
import useUserSettingOptions from '../hooks/useUserSettingOptions';
import { addLedgerTransaction } from '../services/ledgerServices';
import { deriveTransactionType } from '../utils/transactionUtils';
import { showErrorNotification, showSuccessNotification } from '../utils/notificationUtils';
import { useUserContext } from '../contexts/UserContext';
import { useRecurringRuleSubCategoryIds } from '../services/recurringTransactionRuleServices';
import useCurrency from '../hooks/useCurrency';
import { CALCULATION_CURRENCY } from '../utils/currencyUtils';
import TransactionFormModal, { ValidatedTransactionFormInput } from './transactions/TransactionFormModal';

interface QuickAddTransactionModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const QuickAddTransactionModal = ({ isOpen, onClose }: QuickAddTransactionModalProps) => {
    const { t } = useTranslation();
    const { userId } = useUserContext();
    const settings = useSelector(selectSettings);
    const accounts = useSelector(selectAccounts); // Fetch accounts for currency logic
    const options = useUserSettingOptions();
    const [subCategoriesOptions] = options;
    const { baseCurrency } = useCurrency();
    const recSetupCategoriesIds = useRecurringRuleSubCategoryIds(userId);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = async (data: ValidatedTransactionFormInput, isUpdate: boolean, keepOpen?: boolean) => {
        setIsSubmitting(true);
        try {
            await addLedgerTransaction({
                userId: userId || '',
                amount: data.amount,
                date: Timestamp.fromDate(new Date(data.date)), // Explicitly convert to Timestamp to match type definition
                type: deriveTransactionType(data.category),
                status: TransactionStatus.POSTED,
                source: TransactionSource.MANUAL,
                description: data.description || '',
                accountId: data.accountProvenanceId || data.accountReceivableId || '',
                categoryId: data.subCategoryId,
                categoryGroup: data.category as AllTransactionType || OptionalTransactionType.EXPENSES,
                categoryName: subCategoriesOptions.find(o => o.value === data.subCategoryId)?.label || '',
                location: data.location || '',
                currency: baseCurrency?.code || CALCULATION_CURRENCY,
            });

            console.log('Successfully called addLedgerTransaction with data:', {
                ...data,
                date: new Date(data.date)
            });

            showSuccessNotification(t('succeed_record_add'));

            if (!keepOpen) {
                onClose();
            }
        } catch (error: any) {
            console.error('Error adding transaction:', error);
            showErrorNotification(t('failed_record_add', { error: error.message }));
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <TransactionFormModal
            isOpen={isOpen}
            onClose={onClose}
            transaction={null}
            options={options}
            accounts={accounts || []} // Pass accounts
            forbiddenSubCategoryIds={recSetupCategoriesIds}
            onSubmit={onSubmit}
            baseCurrency={baseCurrency?.code}
        />
    );
};

export default QuickAddTransactionModal;
